/**
 * Ajustes → Verifactu (solo ADMIN). Qué enseña: la activación y la remisión, las comprobaciones
 * previas (lo que haría que una emisión se rechazara), los avisos abiertos, cada cadena con su cola,
 * lo pendiente de subsanar, los certificados y la cadena propia congelada. Qué deja hacer (todo
 * queda en la auditoría): remitir ya, subsanar, marcar como conciliado, cerrar una cadena y
 * corregir la razón social del censo. También enlaza la declaración responsable de esta versión.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Send,
  ShieldCheck,
  XCircle,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { RECORD_STATUS_LABEL, refreshVerifactuConfig } from '../../lib/verifactu'
import type { VerifactuStatusDto } from '@toga/shared'

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-ES') : '—')
const ddmmyyyy = (day: string) => day.split('-').reverse().join('/')

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const b = err.body as { error?: string } | undefined
    return b?.error ?? fallback
  }
  return err instanceof Error ? err.message : fallback
}

export default function VerifactuSettingsPage() {
  const [status, setStatus] = useState<VerifactuStatusDto | null>(null)
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setStatus(await api.get<VerifactuStatusDto>('/verifactu/status'))
      setDisabled(false)
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.body as { code?: string } | undefined)?.code === 'VERIFACTU_DISABLED'
      ) {
        setDisabled(true)
      } else {
        setError(errorText(err, 'No se pudo cargar el estado de Verifactu'))
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const act = async (key: string, run: () => Promise<unknown>, done?: string) => {
    setBusy(key)
    setError(null)
    setMessage(null)
    try {
      await run()
      if (done) setMessage(done)
      await reload()
      void refreshVerifactuConfig()
    } catch (err) {
      setError(errorText(err, 'No se pudo completar la acción'))
    } finally {
      setBusy(null)
    }
  }

  if (loading && !status) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando…
      </div>
    )
  }

  if (disabled) {
    return (
      <div className="space-y-3 max-w-3xl">
        <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
          <ShieldCheck size={20} className="text-toga-300" /> Verifactu
        </h2>
        <div className="surface p-4 text-sm t-secondary space-y-2">
          <p>
            Verifactu no está habilitado en este despliegue: Toga emite las facturas con su propia
            cadena de huellas y no las comunica a la AEAT.
          </p>
          <p className="t-muted text-xs">
            Para habilitarlo hay que fijar <span className="font-mono">VERIFACTU_ACTIVE_FROM</span>{' '}
            (el instante de activación, con huso) en la configuración del servidor, junto con el
            certificado y la declaración responsable. Guía paso a paso en{' '}
            <span className="font-mono">docs/verifactu-arranque.md</span>.
          </p>
          <Link
            to="/settings/verifactu-declaration"
            className="text-toga-300 hover:underline text-xs"
          >
            Ver la declaración responsable de esta versión
          </Link>
        </div>
      </div>
    )
  }

  const c = status?.config

  return (
    <div className="space-y-4 max-w-6xl">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
            <ShieldCheck size={20} className="text-toga-300" /> Verifactu
          </h2>
          <p className="text-xs t-muted mt-0.5">
            Registro de facturación y comunicación con la AEAT. Solo lo ven los administradores.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void reload()}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
          >
            <RefreshCw size={14} /> Actualizar
          </button>
          {c?.submissionEnabled && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() =>
                void act('run', () => api.post('/verifactu/run'), 'Pasada de remisión hecha.')
              }
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {busy === 'run' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}{' '}
              Remitir ahora
            </button>
          )}
        </div>
      </header>

      {error && <Banner tone="error">{error}</Banner>}
      {message && <Banner tone="ok">{message}</Banner>}

      {status && status.alerts.length > 0 && (
        <section className="surface p-4 space-y-2">
          <h3 className="t-primary font-semibold text-sm">Avisos abiertos</h3>
          <ul className="space-y-2">
            {status.alerts.map((a) => (
              <li
                key={a.id}
                className="text-xs border-t border-base/40 pt-2 first:border-0 first:pt-0"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="t-primary font-medium">
                      {a.title}
                      {a.occurrences > 1 && <span className="t-faint"> · ×{a.occurrences}</span>}
                    </div>
                    <div className="t-secondary">{a.body}</div>
                    <div className="t-faint">Último: {fmt(a.lastSeenAt)}</div>
                  </div>
                  {a.readAt === null && (
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() =>
                        void act(`read-${a.id}`, () => api.post(`/verifactu/alerts/${a.id}/read`))
                      }
                      className="shrink-0 px-2 py-1 rounded border border-base hover:bg-hover t-secondary"
                    >
                      Marcar como leído
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {c && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card title="Activación" ok={c.active}>
            <p className="font-mono text-xs">{c.activeFrom}</p>
            <p className="text-xs t-muted">
              {c.active
                ? 'Activo: las facturas salen con registro.'
                : 'Aún no ha llegado la fecha.'}
            </p>
          </Card>
          <Card
            title="Remisión a la AEAT"
            ok={c.submissionEnabled || !c.active}
            warn={!c.submissionEnabled}
          >
            <p className="text-xs">{c.submissionEnabled ? 'Encendida' : 'Apagada'}</p>
            <p className="text-xs t-muted">
              Entorno:{' '}
              <span className="font-mono">
                {c.environment === 'test' ? 'pruebas' : 'producción'}
              </span>
            </p>
          </Card>
          <Card
            title="Claves y base de datos"
            ok={c.doctor.ok && (c.keys.ok || !c.submissionEnabled)}
          >
            <p className="text-xs">
              {c.keys.ok
                ? `Claves de certificados: ${c.keys.currentKeyId}`
                : 'Sin claves de certificados'}
            </p>
            <p className="text-xs t-muted">
              {c.doctor.ok
                ? 'Protecciones de la base de datos en orden.'
                : `${c.doctor.problems.length} protección(es) de la base de datos sin comprobar.`}
            </p>
            <p className="text-xs t-faint">
              {c.sif.name} ({c.sif.id}) {c.sif.version} · {c.sif.producer} ({c.sif.producerNif})
            </p>
          </Card>
        </section>
      )}

      {c && (
        <section className="surface p-4 space-y-1.5">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            Declaración responsable
            {c.declaration.status === 'signed' ? (
              <CheckCircle2 size={14} className="text-emerald-300" />
            ) : (
              <AlertTriangle size={14} className="text-amber-300" />
            )}
          </h3>
          <p className="text-xs t-secondary">
            {c.declaration.status === 'signed'
              ? `Firmada el ${ddmmyyyy(c.declaration.signedAt)} en ${c.declaration.place} (versión ${c.declaration.version}).`
              : `Pendiente de firma (versión ${c.declaration.version}).`}
          </p>
          {c.declaration.status === 'pending' && (
            <p className="text-xs t-muted">{c.declaration.reason}</p>
          )}
          <div className="flex gap-2 pt-1 flex-wrap">
            <Link
              to="/settings/verifactu-declaration"
              className="px-2 py-1 rounded text-xs border border-base hover:bg-hover flex items-center gap-1"
            >
              <FileText size={12} /> Ver
            </Link>
            <a
              href="/api/verifactu/declaration.pdf"
              target="_blank"
              rel="noreferrer"
              className="px-2 py-1 rounded text-xs border border-base hover:bg-hover flex items-center gap-1"
            >
              <Download size={12} /> PDF
            </a>
            <a
              href="/api/verifactu/declaration.txt"
              className="px-2 py-1 rounded text-xs border border-base hover:bg-hover flex items-center gap-1"
            >
              <Download size={12} /> TXT
            </a>
          </div>
        </section>
      )}

      {status && status.checks.length > 0 && (
        <section className="surface p-4 space-y-2">
          <h3 className="t-primary font-semibold text-sm">Comprobaciones previas</h3>
          <ul className="space-y-1.5">
            {status.checks.map((k, i) => (
              <li key={`${k.code}-${i}`} className="text-xs flex gap-2">
                {k.level === 'error' ? (
                  <XCircle size={14} className="text-rose-300 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle size={14} className="text-amber-300 shrink-0 mt-0.5" />
                )}
                <span className="t-secondary">
                  <span className="font-mono t-faint">{k.code}</span> · {k.message}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {status && (
        <section className="surface p-4 space-y-3">
          <h3 className="t-primary font-semibold text-sm">Cadenas de registros</h3>
          {status.chains.length === 0 ? (
            <p className="text-xs t-faint">
              Todavía no hay ninguna: nace con la primera factura con registro.
            </p>
          ) : (
            <div className="space-y-3">
              {status.chains.map((chain) => (
                <div
                  key={chain.installationNumber}
                  className="border border-base/60 rounded-lg p-3 text-xs space-y-1"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <span className="font-mono t-primary">{chain.issuerNif}</span>{' '}
                      <span className="t-secondary">{chain.censusName}</span>
                      {chain.closedAt && (
                        <span className="ml-2 px-1.5 py-0.5 rounded border border-base t-faint">
                          cerrada
                        </span>
                      )}
                      <div className="font-mono t-faint">{chain.installationNumber}</div>
                    </div>
                    {!chain.closedAt && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => {
                            const censusName = prompt(
                              'Razón social EXACTA del censo de la AEAT (la AEAT rechaza los envíos si no coincide):',
                              chain.censusName
                            )
                            if (!censusName?.trim()) return
                            void act(
                              `census-${chain.issuerNif}`,
                              () =>
                                api.post(
                                  `/verifactu/installations/${chain.issuerNif}/census-name`,
                                  {
                                    censusName: censusName.trim(),
                                  }
                                ),
                              'Razón social actualizada.'
                            )
                          }}
                          className="px-2 py-1 rounded border border-base hover:bg-hover t-secondary"
                        >
                          Razón social
                        </button>
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => {
                            const reason = prompt(
                              `Cerrar la cadena de ${chain.issuerNif}.\n\nLa próxima emisión abrirá otra con «PrimerRegistro». Una cadena de pruebas se cierra antes de pasar a producción. No se puede deshacer.\n\nMotivo (queda registrado):`
                            )
                            if (!reason?.trim()) return
                            void act(
                              `close-${chain.issuerNif}`,
                              () =>
                                api.post(`/verifactu/installations/${chain.issuerNif}/close`, {
                                  reason: reason.trim(),
                                }),
                              'Cadena cerrada.'
                            )
                          }}
                          className="px-2 py-1 rounded border border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                        >
                          Cerrar cadena
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="t-secondary">
                    Entorno {chain.environment === 'test' ? 'de pruebas' : 'de producción'} · activa
                    desde {chain.activeFrom} · {chain.pending} pendiente(s)
                    {chain.needsAttention > 0 && (
                      <span className="text-amber-300"> · {chain.needsAttention} por revisar</span>
                    )}
                  </div>
                  {chain.incidentSince && (
                    <div className="text-rose-300">Incidencia desde {fmt(chain.incidentSince)}</div>
                  )}
                  <div className="t-faint">
                    {Object.entries(chain.countsByStatus)
                      .map(([k, n]) => `${RECORD_STATUS_LABEL[k] ?? k}: ${n}`)
                      .join(' · ') || 'Sin registros'}
                  </div>
                  {chain.lastSubmission && (
                    <div className="t-faint">
                      Último envío {fmt(chain.lastSubmission.startedAt)} ·{' '}
                      {chain.lastSubmission.estadoEnvio ?? chain.lastSubmission.outcome} ·{' '}
                      {chain.lastSubmission.recordCount} registro(s)
                      {chain.lastSubmission.message ? ` · ${chain.lastSubmission.message}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {status && status.corrections.length > 0 && (
        <section className="surface p-4 space-y-2">
          <h3 className="t-primary font-semibold text-sm">Pendientes de subsanar</h3>
          <p className="text-xs t-muted">
            Registros que la AEAT rechazó o aceptó con errores. «Subsanar» genera el registro
            corregido (si lo impreso no cambia); si lo impreso cambia, lo que procede es una
            rectificativa. «Conciliar» es para lo que decide una persona, con su nota.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="t-faint uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-1 pr-2">Factura</th>
                  <th className="text-left pr-2">Registro</th>
                  <th className="text-left pr-2">Estado</th>
                  <th className="text-left pr-2">Error de la AEAT</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {status.corrections.map((r) => (
                  <tr key={r.recordId} className="border-t border-base/40 align-top">
                    <td className="py-1.5 pr-2 font-mono">
                      {r.invoiceId ? (
                        <Link
                          to={`/billing/${r.invoiceId}`}
                          className="text-toga-300 hover:underline"
                        >
                          {r.number}
                        </Link>
                      ) : (
                        r.number
                      )}
                    </td>
                    <td className="pr-2 t-secondary">
                      #{r.seq} · {r.kind}
                    </td>
                    <td className="pr-2 t-secondary">
                      {RECORD_STATUS_LABEL[r.status] ?? r.status}
                    </td>
                    <td className="pr-2 text-rose-300/90">
                      {r.aeatErrorCode ? `${r.aeatErrorCode}: ` : ''}
                      {r.aeatErrorDescription ?? '—'}
                    </td>
                    <td className="text-right whitespace-nowrap space-x-1">
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() =>
                          void act(
                            `fix-${r.recordId}`,
                            () => api.post(`/verifactu/records/${r.recordId}/correction`),
                            `Subsanación de ${r.number} generada.`
                          )
                        }
                        className="px-2 py-1 rounded border border-base hover:bg-hover t-secondary"
                      >
                        Subsanar
                      </button>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => {
                          const note = prompt(
                            `Marcar ${r.number} como conciliado.\n\nNota obligatoria (queda registrada):`
                          )
                          if (!note?.trim()) return
                          void act(
                            `resolve-${r.recordId}`,
                            () =>
                              api.post(`/verifactu/records/${r.recordId}/resolve`, {
                                note: note.trim(),
                              }),
                            'Registro marcado como conciliado.'
                          )
                        }}
                        className="px-2 py-1 rounded border border-base hover:bg-hover t-secondary"
                      >
                        Conciliar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {status && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="surface p-4 space-y-2">
            <h3 className="t-primary font-semibold text-sm">Certificados</h3>
            {status.certificates.presenters.length === 0 &&
            status.certificates.alerts.length === 0 ? (
              <p className="text-xs t-faint">
                No hay certificados guardados. Se dan de alta por línea de comandos (ver la guía de
                arranque): la clave que los cifra no está en la base de datos.
              </p>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {status.certificates.presenters.map((p) => (
                  <li key={p.issuerNif} className="flex gap-2">
                    {p.ok ? (
                      <CheckCircle2 size={14} className="text-emerald-300 shrink-0" />
                    ) : (
                      <XCircle size={14} className="text-rose-300 shrink-0" />
                    )}
                    <span className="t-secondary">
                      <span className="font-mono">{p.issuerNif}</span>
                      {p.ok ? ` · caduca ${fmt(p.notAfter)}` : ` · ${p.error ?? 'sin certificado'}`}
                    </span>
                  </li>
                ))}
                {status.certificates.alerts.map((a) => (
                  <li key={a.id} className="flex gap-2 text-amber-300">
                    <AlertTriangle size={14} className="shrink-0" />
                    <span>
                      {a.holderName}: {a.status}, caduca {fmt(a.notAfter)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="surface p-4 space-y-2">
            <h3 className="t-primary font-semibold text-sm">Cadena propia anterior (congelada)</h3>
            {status.legacyChain.length === 0 ? (
              <p className="text-xs t-faint">No había facturas con la cadena propia al activar.</p>
            ) : (
              <ul className="space-y-1 text-xs t-secondary">
                {status.legacyChain.map((l) => (
                  <li key={l.seriesPrefix}>
                    Serie <span className="font-mono">{l.seriesPrefix}</span>: {l.invoices}{' '}
                    factura(s)
                    {l.unverified > 0 && (
                      <span className="text-amber-300">
                        {' '}
                        · {l.unverified} con hash no reproducido
                      </span>
                    )}{' '}
                    · congelada {fmt(l.frozenAt)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

function Card({
  title,
  ok,
  warn,
  children,
}: {
  title: string
  ok: boolean
  warn?: boolean
  children: ReactNode
}) {
  return (
    <div className="surface p-4 space-y-1">
      <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
        {title}
        {ok && !warn ? (
          <CheckCircle2 size={14} className="text-emerald-300" />
        ) : (
          <AlertTriangle size={14} className={warn ? 'text-amber-300' : 'text-rose-300'} />
        )}
      </h3>
      {children}
    </div>
  )
}

function Banner({ tone, children }: { tone: 'ok' | 'error'; children: ReactNode }) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={
        tone === 'error'
          ? 'surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30'
          : 'surface p-3 text-sm rounded-lg border text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
      }
    >
      {children}
    </div>
  )
}
